import { Accordion, AccordionDetails, AccordionSummary, Box, Divider, Hidden, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { toSpanishDate } from "../../../helpers/dateFormat";
import { numberFormat } from "../../../helpers/numberFormat";
import { TableLayout } from "../../layout/TableLayout"
import { DivestmentLPTableSmall } from "./DivestmentLPTableSmall";

export const DivestmentLPTable = ({ rows }) => {

    const { t } = useTranslation('global')

    const getTotalsRow = (divestmentList) => {
        let totalAmount = 0;
        let totalShares = 0;
        let totalCapitalGain = 0;

        for (const r of divestmentList) {
            const amountVal = (r.participationValuation * r.divestmentParticipation) * (r.myPercentage / 100);
            const sharesVal = r.divestmentParticipation * (r.myPercentage / 100);
            const gainVal = ((r.divestmentParticipation * r.salesPrice) - (r.participationValuation * r.divestmentParticipation)) * (r.myPercentage / 100);

            if (!isNaN(amountVal)) totalAmount += amountVal;
            if (!isNaN(sharesVal)) totalShares += sharesVal;
            if (!isNaN(gainVal)) totalCapitalGain += gainVal;
        }

        return {
            id: 'Totals',
            investmentDate: '',
            startupAlias: t('totalPosition.realized') || 'Totales Realizados',
            roundNumber: '',
            amount: numberFormat(totalAmount.toFixed(2)),
            participationValuation: null,
            shares: numberFormat(totalShares.toFixed(2)),
            salesPrice: null,
            capitalGain: numberFormat(totalCapitalGain.toFixed(2)),
            divestmentDate: '',
            isTotalRow: true
        };
    };

    const columns = [
        {
            field: 'investmentDate',
            headerName: t('divestment.investmentDate'),
            minWidth: 130,
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => params.row.isTotalRow ? "" : <Typography color='primary' variant='body2'>{toSpanishDate(params.row.investmentDate)}</Typography>
        },
        {
            field: 'startupAlias',
            headerName: t('positions.startup'),
            minWidth: 115,
            flex: 1,
            align: 'center',
            headerAlign: 'center'
        },
        {
            field: 'roundNumber',
            headerName: t('positions.roundNumber'),
            minWidth: 115,
            flex: 1,
            align: 'center',
            headerAlign: 'center'
        },
        {
            field: 'amount',
            headerName: t('positions.amount'),
            minWidth: 130,
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            valueGetter: ({ row }) => row.isTotalRow ? row.amount : numberFormat(((row.participationValuation * row.divestmentParticipation) * (row.myPercentage / 100)).toFixed(2))
        },
        {
            field: 'participationValuation',
            headerName: t('positions.participationValuation'),
            minWidth: 160,
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            valueGetter: (params) => {
                if (params.row.isTotalRow) return '-';
                const resp = params.row.participationValuation;
                return (isNaN(resp) || resp === null || resp === 'Infinity' ? '-' : numberFormat(resp))
            }
        },
        {
            field: 'shares',
            headerName: t('divestment.realizedValue'),
            minWidth: 150,
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            valueGetter: ({ row }) => {
                if (row.isTotalRow) return row.shares;
                const resp = ((row.divestmentParticipation) * (row.myPercentage / 100)).toFixed(2)
                return (isNaN(resp) || resp === null || resp === 'Infinity' ? '-' : numberFormat(resp))
            }
        },
        {
            field: 'salesPrice',
            headerName: t('divestment.salesPrice'),
            minWidth: 130,
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            valueGetter: ({ row }) => {
                if (row.isTotalRow) return '-';
                const resp = numberFormat(row?.salesPrice);
                return (resp === 'NaN' ? '-' : resp)
            }
        },
        {
            field: 'capitalGain',
            headerName: t('positions.capitalGain'),
            minWidth: 140,
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            valueGetter: ({ row }) => {
                if (row.isTotalRow) return row.capitalGain;
                const capitalGain = (row.divestmentParticipation * row.salesPrice) - (row.participationValuation * row.divestmentParticipation)
                return (isNaN(capitalGain) ? '-' : numberFormat((capitalGain * (row.myPercentage / 100)).toFixed(2)))
            }
        },
        {
            field: 'divestmentDate',
            headerName: t('divestment.divestmentDate'),
            minWidth: 145,
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => params.row.isTotalRow ? "" : <Typography color='primary' variant='body2'>{toSpanishDate(params.row.divestmentDate)}</Typography>
        },
    ];

    return (
        <Stack>
            {
                rows.map(fund => (
                    <Accordion key={fund.id}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="panel1a-content"
                            id="panel1a-header"
                        >
                            <Stack width='100%' maxWidth='300px'>
                                <Stack direction='row' flexWrap='wrap' md={{ justifyContent: 'space-between' }}>
                                    <Typography color='primary' marginRight='10px'>{t('divestment.fund')}: </Typography>
                                    <Typography color='secondary'>{fund.name}</Typography>
                                </Stack>
                                <Stack direction='row' flexWrap='wrap' md={{ justifyContent: 'space-between' }}>
                                    <Typography color='primary' marginRight='10px'>{t('divestment.divestor')}:</Typography>
                                    <Typography color='secondary'>{fund.whoInvest}</Typography>
                                </Stack>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Hidden mdDown>
                                <TableLayout
                                    rows={fund.divestment}
                                    columns={columns}
                                    title='noTitle'
                                    pinnedRows={{ bottom: [getTotalsRow(fund.divestment)] }}
                                />
                            </Hidden>
                            <Hidden mdUp>
                                {
                                    fund.divestment.map((divest => {
                                        return (
                                            <Box key={divest.id} >
                                                <Divider sx={{margin: '15px 0px'}}/>
                                                <DivestmentLPTableSmall divest={divest} />
                                            </Box>
                                        )
                                    }))
                                }
                            </Hidden>
                        </AccordionDetails>
                    </Accordion>
                ))
            }

        </Stack>
    )
}